import React, { useState, createContext, useContext, useCallback, useEffect } from 'react';

// Toast Context
const ToastContext = createContext(null);

// Icons
const Icons = {
    success: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#16a34a' }}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
    ),
    error: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#dc2626' }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
    ),
    warning: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ca8a04' }}>
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    info: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#2563eb' }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
    ),
    close: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    )
};

// Toast Item Component
const ToastItem = ({ id, message, type, onClose }) => {
    const [isExiting, setIsExiting] = useState(false);

    const handleClose = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => onClose(id), 300); // Match animation duration
    }, [id, onClose]);

    useEffect(() => {
        const timer = setTimeout(handleClose, 4000);
        return () => clearTimeout(timer);
    }, [handleClose]);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '16px',
                background: 'var(--crm-bg, #ffffff)',
                border: '1px solid var(--crm-gray-200, #e5e7eb)',
                boxShadow: 'var(--crm-shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1))',
                borderRadius: '8px',
                marginBottom: '10px',
                minWidth: '300px',
                maxWidth: '400px',
                animation: isExiting
                    ? 'crm-toast-slide-out 0.3s ease-in forwards'
                    : 'crm-toast-slide-in 0.3s ease-out forwards',
                opacity: isExiting ? 0 : 1,
                transform: `translateX(${isExiting ? '100%' : '0'})`,
                transition: 'all 0.3s ease',
                color: 'var(--crm-gray-900, #1f2937)',
            }}
        >
            <div style={{ flexShrink: 0, marginTop: '2px' }}>
                {Icons[type]}
            </div>
            <div style={{ flex: 1, fontSize: '14px', lineHeight: '1.4' }}>
                {message}
            </div>
            <button
                onClick={handleClose}
                style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    color: 'var(--crm-gray-400, #9ca3af)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    marginLeft: '8px',
                }}
            >
                {Icons.close}
            </button>
        </div>
    );
};

// Toast Container
const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <div style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
        }}>
            {toasts.map(toast => (
                <ToastItem key={toast.id} {...toast} onClose={removeToast} />
            ))}
        </div>
    );
};

// Provider
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'info') => {
        const id = Date.now().toString();
        setToasts(prev => [...prev, { id, message, type }]);
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const showSuccess = useCallback((msg) => showToast(msg, 'success'), [showToast]);
    const showError = useCallback((msg) => showToast(msg, 'error'), [showToast]);
    const showWarning = useCallback((msg) => showToast(msg, 'warning'), [showToast]);
    const showInfo = useCallback((msg) => showToast(msg, 'info'), [showToast]);

    return (
        <ToastContext.Provider value={{ showToast, showSuccess, showError, showWarning, showInfo }}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
            <style>{`
                @keyframes crm-toast-slide-in {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                @keyframes crm-toast-slide-out {
                    from { transform: translateX(0); opacity: 1; }
                    to { transform: translateX(100%); opacity: 0; }
                }
            `}</style>
        </ToastContext.Provider>
    );
};

// Hook
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export default ToastProvider;
